import AppButton from '@nxsys/components/AppButton';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppStepNav from '@nxsys/components/AppStepNav';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import {
  useCreateAgency,
  useCreateAgencyTimesheetValueById,
} from '@nxsys/hooks/api/agency';
import { CreateAgencyPayload } from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';
import { RightOutlined } from '@ant-design/icons';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@nxsys/store';
import { agencyAction } from '@nxsys/store/slices/agency/reducer/agencySlice';
import GeneralInformation, {
  type AgencyGeneralInformationFormValues,
  type GeneralInformationRef,
} from '../detail/GeneralInformation';
import ContactDetail, {
  type ContactDetailFormValues,
  type ContactDetailRef,
} from '../detail/ContactDetail';
import BranchForm, {
  type BranchFormRef,
  type BranchFormValues,
} from '../components/BranchForm';
import PayrollFinanceForm, {
  type PayrollFinanceFormRef,
  type PayrollFinanceFormValues,
} from '../components/PayrollFinanceForm';
import styled from 'styled-components';

const STEP_BASIC_INFO = 0;
const STEP_CONTACT_DETAIL = 1;
const STEP_BRANCHES = 2;
const STEP_PAYROLL_FINANCE = 3;

const ModalBody = styled.div`
  display: flex;
  gap: 24px;
  min-height: 480px;
`;

const StepContent = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 70vh;
  padding-right: 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const FormWrapper = styled.div`
  .ant-form-item .ant-form-item-label {
    display: flex;
    align-items: center;
  }
  .ant-form-item .ant-form-item-label > label {
    min-height: unset;
  }

  .ant-form-item-control-input-content:has(.ant-switch) {
    display: flex;
    justify-content: flex-end;
  }

  width: 100%;
`;

type AddAgencyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const AddAgencyModal: React.FC<AddAgencyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const [step, setStep] = useState(STEP_BASIC_INFO);
  const [step1Values, setStep1Values] =
    useState<AgencyGeneralInformationFormValues | null>(null);
  const [step2Values, setStep2Values] =
    useState<ContactDetailFormValues | null>(null);
  const [step2OnboardingEmails, setStep2OnboardingEmails] = useState<string[]>(
    [],
  );
  const [branchValues, setBranchValues] = useState<BranchFormValues | null>(
    null,
  );
  const [payrollFinanceValues, setPayrollFinanceValues] =
    useState<Partial<PayrollFinanceFormValues> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const generalInfoRef = useRef<GeneralInformationRef>(null);
  const contactDetailRef = useRef<ContactDetailRef>(null);
  const branchFormRef = useRef<BranchFormRef>(null);
  const payrollFinanceFormRef = useRef<PayrollFinanceFormRef>(null);

  const { run: createAgency } = useCreateAgency();
  const { run: createTimesheetValue } = useCreateAgencyTimesheetValueById();

  const stepNavItems = useMemo(
    () => [
      { key: STEP_BASIC_INFO, label: messages['common.generalInformation'] },
      { key: STEP_CONTACT_DETAIL, label: messages['common.contactDetail'] },
      { key: STEP_BRANCHES, label: messages['common.branches'] },
      {
        key: STEP_PAYROLL_FINANCE,
        label: messages['agency.detail.payrollAndFinance'],
      },
    ],
    [messages],
  );

  const resetModal = useCallback(() => {
    setStep(STEP_BASIC_INFO);
    setStep1Values(null);
    setStep2Values(null);
    setStep2OnboardingEmails([]);
    setBranchValues(null);
    setPayrollFinanceValues(null);
  }, []);

  const handleClose = useCallback(() => {
    resetModal();
    onClose();
  }, [onClose, resetModal]);

  const handleStep1Submit = useCallback(
    (values: AgencyGeneralInformationFormValues) => {
      setStep1Values(values);
      setStep(STEP_CONTACT_DETAIL);
    },
    [],
  );

  const handleStep2Submit = useCallback(
    (
      values: ContactDetailFormValues,
      onboardingEmailNotification: string[],
    ) => {
      setStep2Values(values);
      setStep2OnboardingEmails(onboardingEmailNotification);
      setStep(STEP_BRANCHES);
    },
    [],
  );

  const handleBranchSubmit = useCallback((values: BranchFormValues) => {
    setBranchValues(values);
    setStep(STEP_PAYROLL_FINANCE);
  }, []);

  const handlePayrollFinanceSubmit = useCallback(
    async (values: PayrollFinanceFormValues) => {
      if (!step1Values || !step2Values) return;

      setPayrollFinanceValues(values);
      setIsSubmitting(true);

      try {
        const tsApproval = step1Values.timesheetApprovalNeeded;
        const timesheetApprovalNeeded =
          typeof tsApproval === 'boolean'
            ? tsApproval
              ? 'TRUE'
              : 'FALSE'
            : (tsApproval ?? 'NOT_AVAILABLE');

        const payload: CreateAgencyPayload = {
          name: step1Values.agencyName || '',
          agencyPrefix: step1Values.agencyPrefix || '',
          currency: step1Values.currency?.value || '',
          paymentModel: step1Values.paymentModel?.map((x) => x.value) || [],
          allowPortalAccess: !!step1Values.allowPortalAccess,
          enableBenefits: !!step1Values.enableBenefits,
          timesheetApprovalNeeded,
          timesheetApproverIds: [],
          emergencyContact: {
            contactName: step2Values.emergencyContactName || '',
            email: step2Values.emergencyEmail || null,
            telephone: step2Values.emergencyTelephone || null,
            mobile: step2Values.emergencyMobile || null,
          },
          contactDetail: {
            contactName: step2Values.contactName || '',
            address: [
              step2Values.address1 || '',
              step2Values.address2 || '',
              step2Values.address3 || '',
            ].filter(Boolean),
            city: step2Values.city || '',
            county:
              typeof step2Values.county === 'object'
                ? (step2Values.county as any)?.value || ''
                : step2Values.county || '',
            country: step2Values.country?.value || '',
            postCode: step2Values.postCode || '',
            email: step2Values.email || null,
            mobile: step2Values.mobile || null,
            onboardingEmailNotification: step2OnboardingEmails,
          },
          branches:
            branchValues?.siteName ||
            branchValues?.code ||
            branchValues?.VATCode?.value ||
            branchValues?.siteManager?.value
              ? [
                  {
                    name: branchValues.siteName,
                    code: branchValues.code,
                    vatCodeId: branchValues.VATCode?.value || null,
                    siteManagerId: branchValues.siteManager?.value || null,
                    isSameAsAgency: branchValues.isSameAsAgency,
                    ...(branchValues.isSameAsAgency
                      ? {}
                      : {
                          address: {
                            companyName: branchValues.companyName,
                            address: [
                              branchValues.address1,
                              branchValues.address2,
                              branchValues.address3,
                            ].filter(Boolean),
                            city: branchValues.city,
                            county:
                              typeof branchValues.county === 'object'
                                ? (branchValues.county as any)?.value || ''
                                : branchValues.county || '',
                            country: branchValues.country?.value || '',
                            postCode: branchValues.postCode || '',
                          },
                        }),
                  },
                ]
              : [],
          payrollFinance: {
            invoicing: {
              invoicingMethod:
                values.invoicing.invoicingMethod?.value || undefined,
              paymentTerm: values.invoicing.paymentTerm?.value || undefined,
              domesticReverseChargeApplicable:
                values.invoicing.domesticReverseChargeApplicable || undefined,
              vatRate:
                (values.invoicing.vatRate as { value?: string } | null)
                  ?.value || undefined,
            },
            margin: {
              marginMethod: values.margin.marginMethod?.value || undefined,
              margin: values.margin.margin || undefined,
              marginCapped: values.margin.marginCapped || undefined,
              minMargin: values.margin.minMargin || undefined,
            },
            bank: {
              bankName: values.paymentDetails.bankName?.value || undefined,
              accountName: values.paymentDetails.accountName || undefined,
              accountNumber: values.paymentDetails.accountNumber || undefined,
              sortCode: values.paymentDetails.sortCode || undefined,
              buildingSocietyNumber:
                values.paymentDetails.buildingSocietyNumber || undefined,
              IBANReference: values.paymentDetails.IBANReference || undefined,
              SWIFTNo: values.paymentDetails.SWIFTNo || undefined,
            },
            holidayPay: {
              holidayPayMethod:
                values.holidayPay.holidayPayMethod?.value || undefined,
              umbrellaHolidayPayMethod:
                values.holidayPay.umbrellaHolidayPayMethod?.value || undefined,
              payeHolidayPayRate:
                values.holidayPay.payeHolidayPayRate &&
                !isNaN(Number(values.holidayPay.payeHolidayPayRate))
                  ? Number(values.holidayPay.payeHolidayPayRate)
                  : undefined,
              umbrellaHolidayPayRate:
                values.holidayPay.umbrellaHolidayPayRate &&
                !isNaN(Number(values.holidayPay.umbrellaHolidayPayRate))
                  ? Number(values.holidayPay.umbrellaHolidayPayRate)
                  : undefined,
            },
          },
        };

        const res = await createAgency(payload);
        const newAgencyId = res?.data?.id;

        // Create margin table entries sequentially to avoid race conditions
        const marginEntries = values.margin.marginTableEntries;
        if (newAgencyId && marginEntries?.length) {
          for (const entry of marginEntries) {
            await createTimesheetValue(newAgencyId, {
              fromValue: entry.fromValue,
              toValue: entry.toValue,
              marginAmount: entry.marginAmount,
              marginPercent: entry.marginPercent,
            });
          }
        }

        showMessage({
          type: 'success',
          content: messages['prospect.agency.createSuccess'],
        });
        dispatch(agencyAction.getAgencyList({ page: 0, size: 20 }));
        handleClose();
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      branchValues,
      createAgency,
      dispatch,
      handleClose,
      messages,
      onSuccess,
      step1Values,
      step2OnboardingEmails,
      step2Values,
    ],
  );

  const handleSaveContinue = useCallback(() => {
    if (step === STEP_BASIC_INFO) {
      generalInfoRef.current?.submit();
    } else if (step === STEP_CONTACT_DETAIL) {
      contactDetailRef.current?.submit();
    } else if (step === STEP_BRANCHES) {
      branchFormRef.current?.submit();
    } else if (step === STEP_PAYROLL_FINANCE) {
      payrollFinanceFormRef.current?.submit();
    }
  }, [step]);

  const handleBack = useCallback(() => {
    if (step === STEP_CONTACT_DETAIL) {
      setStep2Values(null);
      setStep2OnboardingEmails([]);
    }
    setStep(step - 1);
  }, [step]);

  const renderStepContent = () => {
    switch (step) {
      case STEP_BASIC_INFO:
        return (
          <GeneralInformation
            ref={generalInfoRef}
            mode="create"
            data={null}
            initialValues={step1Values ?? undefined}
            onSubmit={handleStep1Submit}
            hideActions
          />
        );
      case STEP_CONTACT_DETAIL:
        return (
          <ContactDetail
            ref={contactDetailRef}
            mode="create"
            data={null}
            initialValues={step2Values ?? undefined}
            initialOnboardingEmails={step2OnboardingEmails}
            onSubmit={handleStep2Submit}
            hideActions
          />
        );
      case STEP_BRANCHES:
        return (
          <div className="px-6 py-6">
            <div className="mb-6 text-xl font-medium text-gray-800">
              {messages['common.branches'] ?? 'Sites'}
            </div>
            <BranchForm formRef={branchFormRef} onSubmit={handleBranchSubmit} />
          </div>
        );
      case STEP_PAYROLL_FINANCE:
        return (
          <PayrollFinanceForm
            mode="create"
            formRef={payrollFinanceFormRef}
            defaultValues={payrollFinanceValues ?? undefined}
            onSubmit={handlePayrollFinanceSubmit}
          />
        );
      default:
        return null;
    }
  };

  const footer = (
    <ModalFooter>
      <div>
        {step > STEP_BASIC_INFO && (
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.back'] ?? 'Back'}
            onClick={handleBack}
            disabled={isSubmitting}
          />
        )}
      </div>
      <AppButton
        type="primary"
        name={
          step === STEP_PAYROLL_FINANCE
            ? (messages['common.createAgency'] ?? 'Create Agency')
            : (messages['common.saveAndContinue'] ?? 'Save & Continue')
        }
        rightIcon={
          step === STEP_PAYROLL_FINANCE ? undefined : <RightOutlined />
        }
        onClick={handleSaveContinue}
        loading={isSubmitting}
        disabled={isSubmitting}
      />
    </ModalFooter>
  );

  return (
    <AppModalContainer
      isOpen={isOpen}
      toggle={handleClose}
      title={messages['prospect.addNewAgency'] ?? 'Add Agency'}
      width={900}
      footer={footer}
      closable
    >
      <ModalBody>
        <AppStepNav
          steps={stepNavItems}
          currentStep={step}
          showActiveIndicator
        />
        <FormWrapper>
          <StepContent>{renderStepContent()}</StepContent>
        </FormWrapper>
      </ModalBody>
    </AppModalContainer>
  );
};

export default AddAgencyModal;
